/**
 * UTV AL stairs: plate stringers, checker steps, rest platforms and handrails.
 *
 * UTV ×2000 (T-4102302) latches on the lower transom at level `p` and the upper
 * one at `p+4`, c/c the stair length, with landings at both ends and a 44°
 * flight of ten 200 mm steps between them. The small 500/1000/1500 flights hook
 * on one beam edge and descend outward at the same pitch.
 */

import { ExtrudeGeometry, Group, Mesh, Shape } from "three";
import { CFG, GUARDRAIL_MID_HEIGHT, GUARDRAIL_TOP_HEIGHT, KR, MM, nodeY } from "../../data/haki.ts";
import type { SmallStair, Stair, UtvStair } from "../../model/types.ts";
import { isUtvStair } from "../../model/types.ts";
import { G } from "../geometries.ts";
import { M } from "../materials.ts";
import { boxAt, slopedBox, tubeBetween, V, yawOf } from "../threeHelpers.ts";
import { railPlan } from "../tools/describe.ts";
import { DECK_LIFT, PLANK_THICKNESS, type RenderContext } from "./context.ts";

/** Landing length at each end of a UTV ×2000, metres. */
const LANDING = 0.45;

/** Steps in a UTV ×2000 flight — 10 × 200 mm = 2000 mm rise. */
const UTV_STEPS = 10;

/** Tread and landing plate thickness, metres. */
const TREAD = 0.032;

/** Stringer plate: its height, thickness, and how far its top edge rises above the tread nosings. */
const STRINGER_HEIGHT = 0.26;
const STRINGER_THICKNESS = 0.028;
const STRINGER_ABOVE_NOSING = 0.08;

/** At the upper landing the stringer's top edge is capped this far above the floor, metres. */
const STRINGER_CAP = 0.1;

/** Stringer profiles depend only on the stair size, so each is built once. */
const STRINGER_GEOMETRIES = new Map<string, ExtrudeGeometry>();

type StringerProfile = Readonly<{
  /** Top edge of the plate, from the low end to the high end. */
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  height: number;
  /** The high end's top corner is cut down to this; the low end's bottom corner up to `yMin`. */
  yMax: number;
  yMin: number;
}>;

/**
 * The side profile of a plate stringer: the band under its top edge with both
 * ends cut vertically at the landings, capped horizontally where a corner
 * would otherwise poke past a landing — a rotated box leaves those corners.
 */
function stringerPoints(profile: StringerProfile): [number, number][] {
  const { x0, y0, x1, y1, height, yMax, yMin } = profile;
  const slope = (y1 - y0) / (x1 - x0);
  const top = (x: number): number => y0 + slope * (x - x0);
  const [xLow, xHigh] = y1 > y0 ? [x0, x1] : [x1, x0];
  const points: [number, number][] = [[xLow, top(xLow)]];

  if (top(xHigh) > yMax) {
    points.push([x0 + (yMax - y0) / slope, yMax], [xHigh, yMax]);
  } else {
    points.push([xHigh, top(xHigh)]);
  }
  points.push([xHigh, top(xHigh) - height]);
  if (top(xLow) - height < yMin) {
    points.push([x0 + (yMin + height - y0) / slope, yMin], [xLow, yMin]);
  } else {
    points.push([xLow, top(xLow) - height]);
  }

  const area = points.reduce((sum, [x, y], i) => {
    const [nx, ny] = points[(i + 1) % points.length] ?? [x, y];
    return sum + x * ny - nx * y;
  }, 0);

  return area < 0 ? points.reverse() : points;
}

function stringerGeometry(profile: StringerProfile): ExtrudeGeometry {
  const key = Object.values(profile)
    .map((value) => value.toFixed(3))
    .join("|");
  const cached = STRINGER_GEOMETRIES.get(key);
  if (cached) {
    return cached;
  }

  const shape = new Shape();
  stringerPoints(profile).forEach(([x, y], index) => {
    if (index === 0) {
      shape.moveTo(x, y);
    } else {
      shape.lineTo(x, y);
    }
  });
  const geometry = new ExtrudeGeometry(shape, { depth: STRINGER_THICKNESS, bevelEnabled: false });
  geometry.translate(0, 0, -STRINGER_THICKNESS / 2);
  STRINGER_GEOMETRIES.set(key, geometry);

  return geometry;
}

/** A stringer plate at lateral offset `z`, its profile drawn relative to `baseY`. */
function stringerMesh(profile: StringerProfile, baseY: number, z: number): Mesh {
  const mesh = new Mesh(stringerGeometry(profile), M.aluD);
  mesh.position.set(0, baseY, z);
  mesh.castShadow = true;
  mesh.receiveShadow = true;

  return mesh;
}

/** The hooks sit this far above a transom's cup — where the castings latch. */
const HOOK_ABOVE_CUP = 0.03;

/** Handrail post pitch, metres (T-4058300). */
const POST_PITCH = 0.8;

function floorTop(p: number): number {
  return nodeY(p) + DECK_LIFT + PLANK_THICKNESS;
}

/** The stringer offsets a handrail sits at, paired with which part goes there. */
function railMounts(
  rail: Stair["rail"],
  halfWidth: number,
  inset: number,
): { z: number; kind: "hl" | "hli" }[] {
  const plan = railPlan(rail);
  const mounts: { z: number; kind: "hl" | "hli" }[] = [];

  if (plan.left) {
    mounts.push({ z: -halfWidth + inset, kind: plan.left });
  }
  if (plan.right) {
    mounts.push({ z: halfWidth - inset, kind: plan.right });
  }

  return mounts;
}

function renderSmallStair(ctx: RenderContext, stair: SmallStair, group: Group): void {
  const mx = (stair.a.x + stair.b.x) / 2;
  const mz = (stair.a.z + stair.b.z) / 2;
  const rise = stair.small * MM;
  const run = rise * 1.036;

  group.position.set(mx, 0, mz);
  group.rotation.y = yawOf(stair.dx, stair.dz) - Math.PI / 2;

  const width = CFG.utv.width * MM;
  const yTop = floorTop(stair.p);
  const yBottom = yTop - rise;
  const x0 = 0.05;
  const steps = Math.max(2, Math.round(stair.small / 200));
  const nosing = rise / steps / 2 + STRINGER_ABOVE_NOSING;

  for (const z of [-width / 2 + 0.012, width / 2 - 0.012]) {
    group.add(
      stringerMesh(
        { x0, y0: nosing, x1: x0 + run, y1: nosing - rise, height: 0.24, yMax: nosing, yMin: -rise - 0.02 },
        yTop,
        z,
      ),
    );
    group.add(boxAt(x0 - 0.02, yTop - 0.03, z, 0.1, 0.08, 0.05, M.cast, 0));
  }

  // Equal risers from the deck floor down; the last tread is level with the entry plate.
  for (let i = 1; i <= steps; i++) {
    const x = x0 + run * ((i - 0.5) / steps);
    const top = yTop - (rise * i) / steps;
    group.add(boxAt(x, top - TREAD / 2, 0, run / steps + 0.03, TREAD, width - 0.08, M.alu, 0));
    group.add(boxAt(x, top + 0.003, 0, run / steps - 0.02, 0.006, width - 0.14, M.grip, 0));
  }
  group.add(boxAt(x0 + run + 0.1, yBottom - 0.01, 0, 0.25, 0.02, width - 0.06, M.alu, 0));

  const fzv = CFG.fzv[stair.small];
  if (fzv) {
    for (const { z } of railMounts(stair.rail, width / 2, 0.012)) {
      const postY = yBottom + 0.02;
      group.add(
        tubeBetween(V(x0 + run - 0.05, postY, z), V(x0 + run - 0.05, postY + 0.95, z), G.tube15, M.alu),
      );
      group.add(
        tubeBetween(V(x0 + run - 0.05, postY + 0.95, z), V(x0 - 0.15, yTop + 0.95, z), G.tube15, M.alu),
      );
      group.add(
        tubeBetween(V(x0 + run - 0.05, postY + 0.5, z), V(x0 - 0.15, yTop + 0.5, z), G.tube15, M.alu),
      );
      group.add(tubeBetween(V(x0 - 0.15, yTop + 0.95, z), V(x0 - 0.55, yTop + 0.95, z), G.tube15, M.alu));
      group.add(tubeBetween(V(x0 - 0.15, yTop + 0.5, z), V(x0 - 0.15, yTop + 0.95, z), G.tube15, M.alu));
      ctx.bom.add("Access", `UTV AL handrail ${stair.small} Fzv`, fzv.art, fzv.kg);
    }
  }

  const art = CFG.utv.art[stair.small];
  const kg = CFG.utv.kg[stair.small];
  if (art !== undefined && kg !== undefined) {
    ctx.bom.add("Access", `UTV stair ${stair.small} AL`, art, kg);
  }
}

/** The sloped double rail with horizontal returns over both landings. */
/**
 * The handrails along a UTV ×2000.
 *
 * HL (T-4058300) is the full outer rail: posts every 800 mm and horizontal
 * returns over both landings. HLI is the shorter inner one — two end posts and
 * the sloped rails over the flight only, no returns.
 */
function renderUtvHandrail(
  group: Group,
  stair: UtvStair,
  geometry: { len: number; width: number; floorLo: number; floorHi: number; x0: number; x1: number },
): void {
  const { len, width, floorLo, floorHi, x0, x1 } = geometry;
  const run = x1 - x0;
  const rise = floorHi - floorLo;
  /** Walking surface under a post: landing, or the flight between them. */
  const floorAt = (x: number): number => {
    if (x <= x0) {
      return floorLo;
    }
    if (x >= x1) {
      return floorHi;
    }

    return floorLo + (rise * (x - x0)) / run;
  };

  for (const { z, kind } of railMounts(stair.rail, width / 2, 0.01)) {
    if (kind === "hli") {
      for (const h of [GUARDRAIL_MID_HEIGHT, GUARDRAIL_TOP_HEIGHT]) {
        group.add(tubeBetween(V(x0, floorLo + h, z), V(x1, floorHi + h, z), G.tube15, M.alu));
      }
      group.add(
        tubeBetween(V(x0, floorLo + 0.02, z), V(x0, floorLo + GUARDRAIL_TOP_HEIGHT, z), G.tube15, M.alu),
      );
      group.add(
        tubeBetween(V(x1, floorHi + 0.02, z), V(x1, floorHi + GUARDRAIL_TOP_HEIGHT, z), G.tube15, M.alu),
      );
      continue;
    }

    const posts = Math.max(3, Math.round(len / POST_PITCH));
    for (let i = 0; i <= posts; i++) {
      const x = 0.06 + ((len - 0.12) * i) / posts;
      group.add(
        tubeBetween(V(x, floorAt(x) + 0.02, z), V(x, floorAt(x) + GUARDRAIL_TOP_HEIGHT, z), G.tube15, M.alu),
      );
    }

    for (const h of [GUARDRAIL_MID_HEIGHT, GUARDRAIL_TOP_HEIGHT]) {
      group.add(tubeBetween(V(0.06, floorLo + h, z), V(x0, floorLo + h, z), G.tube15, M.alu));
      group.add(tubeBetween(V(x0, floorLo + h, z), V(x1, floorHi + h, z), G.tube15, M.alu));
      group.add(tubeBetween(V(x1, floorHi + h, z), V(len - 0.06, floorHi + h, z), G.tube15, M.alu));
    }
  }
}

function renderUtvStair(ctx: RenderContext, stair: UtvStair, group: Group): void {
  const len = stair.utv * MM;
  const width = CFG.utv.width * MM;

  group.position.set(stair.o.x, 0, stair.o.z);
  group.rotation.y = yawOf(stair.dx, stair.dz) - Math.PI / 2;

  // The hooks latch the transoms; every walking surface sits at deck-floor
  // height, so both landings meet the planks flush and the flight climbs from
  // one floor to the other in ten equal risers.
  const hookLo = nodeY(stair.p) + KR + HOOK_ABOVE_CUP;
  const hookHi = nodeY(stair.p + 4) + KR + HOOK_ABOVE_CUP;
  const floorLo = floorTop(stair.p);
  const floorHi = floorTop(stair.p + 4);
  const x0 = LANDING;
  const x1 = len - LANDING;
  const run = x1 - x0;
  const rise = floorHi - floorLo;
  const riser = rise / UTV_STEPS;
  // Tread tops lie half a riser above the landing-to-landing line; the stringer hangs from just above them.
  const nosing = riser / 2 + STRINGER_ABOVE_NOSING;
  const stringer: StringerProfile = {
    x0,
    y0: nosing,
    x1,
    y1: rise + nosing,
    height: STRINGER_HEIGHT,
    yMax: rise + STRINGER_CAP,
    yMin: hookLo - floorLo,
  };

  const ends = [
    { cx: LANDING / 2, hookX: 0.02, hookY: hookLo, floorY: floorLo },
    { cx: len - LANDING / 2, hookX: len - 0.02, hookY: hookHi, floorY: floorHi },
  ];

  for (const z of [-width / 2 + 0.02, width / 2 - 0.02]) {
    group.add(stringerMesh(stringer, floorLo, z));
    for (const end of ends) {
      const plateHeight = end.floorY - end.hookY + 0.02;
      group.add(
        slopedBox(end.cx, end.hookY + plateHeight / 2 - 0.01, z, LANDING + 0.04, plateHeight, 0.02, 0, M.alu),
      );
      group.add(boxAt(end.hookX, end.hookY + 0.02, z, 0.12, 0.09, 0.05, M.cast, 0));
    }
  }

  for (const end of ends) {
    group.add(boxAt(end.cx, end.floorY - TREAD / 2, 0, LANDING - 0.02, TREAD, width - 0.06, M.alu, 0));
    group.add(boxAt(end.cx, end.floorY + 0.003, 0, LANDING - 0.06, 0.006, width - 0.12, M.grip, 0));
  }

  // The top tread is level with the upper landing, so the surface runs on unbroken.
  for (let i = 1; i <= UTV_STEPS; i++) {
    const x = x0 + run * ((i - 0.5) / UTV_STEPS);
    const top = floorLo + riser * i;
    group.add(boxAt(x, top - TREAD / 2, 0, run / UTV_STEPS + 0.03, TREAD, width - 0.08, M.alu, 0));
    group.add(boxAt(x, top + 0.003, 0, run / UTV_STEPS - 0.02, 0.006, width - 0.14, M.grip, 0));
  }

  renderUtvHandrail(group, stair, { len, width, floorLo, floorHi, x0, x1 });

  const mod = stair.utv;
  ctx.bom.add("Access", `UTV stair ${mod}×2000 AL`, CFG.utv.art[mod] ?? "—", CFG.utv.kg[mod] ?? 29.2);

  const plan = railPlan(stair.rail);
  for (const kind of [plan.left, plan.right]) {
    if (kind === "hl") {
      ctx.bom.add("Access", `Handrail HL ${mod}×2000 AL`, CFG.hl.art[mod] ?? "—", CFG.hl.kg[mod] ?? 10.3);
    } else if (kind === "hli") {
      ctx.bom.add("Access", "Handrail inner HLI UTV AL", CFG.hli.art, CFG.hli.kg);
    }
  }
}

export function renderStairs(ctx: RenderContext): void {
  ctx.model.stairs.forEach((stair, idx) => {
    const group = new Group();

    if (isUtvStair(stair)) {
      renderUtvStair(ctx, stair, group);
    } else {
      renderSmallStair(ctx, stair, group);
    }

    for (const child of group.children) {
      child.userData = { type: "stair", idx };
      if (child instanceof Mesh) {
        ctx.pickElems.push(child);
      }
    }
    ctx.root.add(group);
  });
}
